import { useId } from 'react';
import type { TextAreaProps, TextAreaResize } from './types';

// ─── Resize class map ─────────────────────────────────────────────────────────

const RESIZE_CLASSES: Record<TextAreaResize, string> = {
  none: 'resize-none',
  vertical: 'resize-y',
  both: 'resize',
};

// ─── TextArea ─────────────────────────────────────────────────────────────────

export function TextArea({
  value = '',
  onChange,
  label,
  placeholder,
  rows = 3,
  maxLength,
  error,
  disabled = false,
  required = false,
  resize = 'vertical',
  id: idProp,
  name,
  className,
  'aria-label': ariaLabel,
}: TextAreaProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const errorId = error ? `${id}-error` : undefined;
  const counterId = maxLength !== undefined ? `${id}-counter` : undefined;

  const hasError = Boolean(error);
  const charCount = value.length;
  const isNearLimit = maxLength !== undefined && charCount >= maxLength * 0.9;
  const isAtLimit = maxLength !== undefined && charCount >= maxLength;

  const describedBy = [errorId, counterId].filter(Boolean).join(' ') || undefined;

  const textareaClasses = [
    'w-full rounded border bg-white px-3 py-2 text-sm text-neutral-900 outline-none transition-colors duration-150',
    'dark:bg-neutral-800 dark:text-neutral-100',
    'placeholder:text-neutral-400 dark:placeholder:text-neutral-500',
    RESIZE_CLASSES[resize],
    hasError
      ? 'border-danger-500 focus:ring-2 focus:ring-danger-500 focus:ring-offset-0'
      : 'border-neutral-300 dark:border-neutral-600 hover:border-neutral-400 dark:hover:border-neutral-500 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 focus:ring-offset-0',
    disabled
      ? 'bg-neutral-100 dark:bg-neutral-700 cursor-not-allowed opacity-70'
      : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={['flex flex-col gap-1', className ?? ''].filter(Boolean).join(' ')}>
      {label && (
        <label
          htmlFor={id}
          className="text-sm font-medium text-neutral-700 dark:text-neutral-300"
        >
          {label}
          {required && (
            <span className="text-danger-500 ml-1" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}

      <textarea
        id={id}
        name={name}
        value={value}
        rows={rows}
        maxLength={maxLength}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        aria-label={ariaLabel}
        aria-required={required || undefined}
        aria-invalid={hasError || undefined}
        aria-describedby={describedBy}
        onChange={(e) => onChange?.(e.target.value)}
        className={textareaClasses}
      />

      <div className="flex items-start justify-between gap-2">
        {hasError ? (
          <span id={errorId} role="alert" className="text-xs text-danger-600 dark:text-danger-400">
            {error}
          </span>
        ) : (
          <span />
        )}

        {maxLength !== undefined && (
          <span
            id={counterId}
            aria-live="polite"
            className={[
              'text-xs shrink-0',
              isAtLimit
                ? 'text-danger-500 dark:text-danger-400'
                : isNearLimit
                  ? 'text-warning-500 dark:text-warning-400'
                  : 'text-neutral-400 dark:text-neutral-500',
            ].join(' ')}
          >
            {charCount}/{maxLength}
          </span>
        )}
      </div>
    </div>
  );
}
